import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreDocument, AngularFirestoreCollection } from '@angular/fire/firestore';
import { AngularFireAuth } from '@angular/fire/auth';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import firebase from "firebase/app";

import {
  UserAuditLog,
  AuditActionType,
  AuditStatus,
  AuditSeverity,
  AuditCategory,
  AuditLogFilter,
  AuditLogSummary,
  PaginatedAuditLogs
} from '../models/audit.model';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuditService {

  private auditLogsCollection: AngularFirestoreCollection<UserAuditLog>;
  private auditLogs$: Observable<UserAuditLog[]>;

  private currentAccount: any;

  constructor(
    private afs: AngularFirestore,
    private afAuth: AngularFireAuth
  ) {
    this.auditLogsCollection = this.afs.collection<UserAuditLog>('audit_logs');

    // No need to maintain currentUser state, we'll fetch it when needed

    // Get current account from localStorage
    this.currentAccount = JSON.parse(localStorage.getItem('account') || '{}');
  }

  /**
   * Log a user action for audit purposes
   */
  async logAction(auditData: Partial<UserAuditLog>): Promise<void> {
    try {
      console.log('🔍 AuditService.logAction called with:', auditData);

      // Get current user context if not provided
      const userContext = await this.getCurrentUserContext();
      console.log('👤 User context:', userContext);

      const auditLog: UserAuditLog = {
        id: this.afs.createId(),
        uid: userContext.uid,
        account_key: auditData.account_key || userContext.account_key || "",
        user_id: userContext.uid,
        user_email: userContext.email,
        user_name: userContext.displayName || userContext.fullName,
        user_role: this.getUserRoleString(userContext.roles),

        // Action details
        action_type: auditData.action_type,
        action_description: auditData.action_description,
        resource_type: auditData.resource_type,
        resource_id: auditData.resource_id ?? "",
        resource_name: auditData.resource_name ?? "",

        // Data changes
        old_values: auditData.old_values ?? "",
        new_values: auditData.new_values ?? "",
        changes_summary: auditData.changes_summary ?? "",

        // Request context
        ip_address: await this.getClientIP(),
        user_agent: navigator.userAgent,
        session_id: this.getSessionId(),
        route_path: window.location.pathname,

        // Timestamps
        timestamp: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        date_created: new Date(),

        // Metadata
        status: auditData.status || AuditStatus.SUCCESS,
        severity: auditData.severity || this.determineSeverity(auditData.action_type),
        category: auditData.category || this.determineCategory(auditData.action_type),
        tags: auditData.tags || [],
        notes: auditData.notes ?? ""
      };

      console.log(auditLog);
      
      // Save to Firestore
      console.log('💾 Attempting to save audit log to Firestore:', auditLog.id);
      await this.auditLogsCollection.doc(auditLog.id).set(auditLog);

      console.log('✅ Audit log successfully created:', auditLog);
    } catch (error) {
      console.error('❌ Error creating audit log:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });

      // Don't throw the error to avoid breaking the user's flow
      // but log it for debugging
    }
  }

  /**
   * Log authentication events
   */
  async logAuthentication(action: AuditActionType, details?: Partial<UserAuditLog>): Promise<void> {
    await this.logAction({
      action_type: action,
      action_description: this.getActionDescription(action),
      resource_type: 'authentication',
      category: AuditCategory.AUTHENTICATION,
      severity: action === AuditActionType.LOGIN_FAILED ? AuditSeverity.MEDIUM : AuditSeverity.LOW,
      status: action === AuditActionType.LOGIN_FAILED ? AuditStatus.FAILED : AuditStatus.SUCCESS,
      ...details
    });
  }

  /**
   * Log CRUD operations
   */
  async logCRUD(
    action: AuditActionType.CREATE | AuditActionType.READ | AuditActionType.UPDATE | AuditActionType.DELETE,
    resourceType: string,
    resourceId: string,
    resourceName?: string,
    oldValues?: any,
    newValues?: any
  ): Promise<void> {
    const changesSummary = action === AuditActionType.UPDATE ?
      this.generateChangesSummary(oldValues, newValues) : undefined;

    await this.logAction({
      action_type: action,
      action_description: `${action} ${resourceType}`,
      resource_type: resourceType,
      resource_id: resourceId,
      resource_name: resourceName,
      old_values: oldValues,
      new_values: newValues,
      changes_summary: changesSummary,
      category: AuditCategory.DATA_MODIFICATION,
      severity: action === AuditActionType.DELETE ? AuditSeverity.HIGH : AuditSeverity.MEDIUM
    });
  }

  /**
   * Log page navigation
   */
  async logNavigation(routePath: string, pageTitle?: string): Promise<void> {
    await this.logAction({
      action_type: AuditActionType.PAGE_VIEW,
      action_description: `Accessed ${pageTitle || routePath}`,
      resource_type: 'navigation',
      resource_id: routePath,
      resource_name: pageTitle,
      route_path: routePath,
      category: AuditCategory.SYSTEM_ACCESS,
      severity: AuditSeverity.LOW
    });
  }

  /**
   * Log data exports
   */
  async logExport(exportType: string, filename?: string, recordCount?: number): Promise<void> {
    await this.logAction({
      action_type: AuditActionType.EXPORT,
      action_description: `Exported ${exportType} data`,
      resource_type: 'export',
      resource_name: filename,
      notes: recordCount ? `Exported ${recordCount} records` : undefined,
      category: AuditCategory.DATA_MODIFICATION,
      severity: AuditSeverity.MEDIUM
    });
  }

  /**
   * Get audit logs with filtering
   */
  getAuditLogs(filter?: AuditLogFilter): Observable<UserAuditLog[]> {
    console.log('🔍 getAuditLogs called with filter:', filter);

    return this.afs.collection('audit_logs', ref => {
      let query: any = ref;
      let hasWhereClause = false;

      // Apply filters carefully - skip empty/undefined filters
      if (filter?.account_key && filter.account_key.trim() !== '') {
        console.log('🔽 Adding account_key filter:', filter.account_key);
        query = query.where('account_key', '==', filter.account_key);
        hasWhereClause = true;
      }

      if (filter?.user_id && filter.user_id.trim() !== '') {
        console.log('🔽 Adding user_id filter:', filter.user_id);
        query = query.where('user_id', '==', filter.user_id);
        hasWhereClause = true;
      }

      if (filter?.action_types && filter.action_types.length > 0) {
        console.log('🔽 Adding action_types filter:', filter.action_types);
        query = query.where('action_type', 'in', filter.action_types.slice(0, 10)); // Firestore limit
        hasWhereClause = true;
      }

      if (filter?.categories && filter.categories.length > 0) {
        console.log('🔽 Adding categories filter:', filter.categories);
        query = query.where('category', 'in', filter.categories.slice(0, 10));
        hasWhereClause = true;
      }

      // Handle date filters more carefully
      if (filter?.start_date) {
        console.log('🔽 Adding start_date filter:', filter.start_date);
        query = query.where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(filter.start_date));
        hasWhereClause = true;
      }

      if (filter?.end_date) {
        console.log('🔽 Adding end_date filter:', filter.end_date);
        query = query.where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(filter.end_date));
        hasWhereClause = true;
      }

      // Only order by timestamp if we have a where clause, or if no filters applied
      try {
        if (hasWhereClause || !filter) {
          console.log('🔽 Adding orderBy timestamp desc');
          query = query.orderBy('timestamp', 'desc');
        } else {
          // If no filters, try ordering by a different field or use a simple limit
          console.log('🔽 No where clause, using simple query');
          query = query.orderBy('date_created', 'desc');
        }
      } catch (error) {
        console.warn('⚠️ Error adding orderBy, using simple query:', error);
      }

      // Apply limit
      const limit = filter?.limit || 100; // Default smaller limit for better performance
      console.log('🔽 Adding limit:', limit);
      query = query.limit(limit);

      console.log('🔽 Final query configuration complete');
      return query;
    }).valueChanges().pipe(
      map((logs: UserAuditLog[]) => {
        console.log('📋 Query returned logs:', logs?.length || 0, 'documents');
        if (logs?.length > 0) {
          console.log('📄 Sample log structure:', logs[0]);
        }
        return logs || [];
      })
    );
  }

  /**
   * Get all audit logs without any filters (for debugging)
   */
  getAllAuditLogsRaw(): Observable<UserAuditLog[]> {
    console.log('🔍 Getting ALL audit logs (no filters)...');

    return this.afs.collection('audit_logs').valueChanges().pipe(
      map((logs: UserAuditLog[]) => {
        console.log('📋 Raw query returned:', logs?.length || 0, 'total documents');
        return logs || [];
      })
    );
  }

  /**
   * Get audit logs with cursor-based pagination (efficient for large datasets)
   * Optimized for performance with proper error handling
   */
  async getAuditLogsPaginated(filter?: AuditLogFilter): Promise<PaginatedAuditLogs> {
    const startTime = performance.now();
    const pageSize = filter?.limit || 10;

    try {
      const db = firebase.firestore();
      let query: firebase.firestore.Query = db.collection('audit_logs');

      // Apply equality filters first (better index utilization)
      if (filter?.account_key?.trim()) {
        query = query.where('account_key', '==', filter.account_key.trim());
      }

      if (filter?.user_id?.trim()) {
        query = query.where('user_id', '==', filter.user_id.trim());
      }

      // 'in' queries (limited to 10 values by Firestore)
      if (filter?.action_types?.length > 0) {
        query = query.where('action_type', 'in', filter.action_types.slice(0, 10));
      }

      if (filter?.categories?.length > 0) {
        query = query.where('category', 'in', filter.categories.slice(0, 10));
      }

      // Range filters (must come after equality filters for same field)
      if (filter?.start_date) {
        query = query.where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(filter.start_date));
      }

      if (filter?.end_date) {
        query = query.where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(filter.end_date));
      }

      // Order by timestamp descending (required for cursor pagination)
      query = query.orderBy('timestamp', 'desc');

      // Apply cursor for pagination - fetch cursor doc in parallel if needed
      if (filter?.lastDocId) {
        try {
          const lastDocSnapshot = await db.collection('audit_logs').doc(filter.lastDocId).get();
          if (lastDocSnapshot.exists) {
            query = query.startAfter(lastDocSnapshot);
          }
        } catch (cursorError) {
          console.warn('⚠️ Could not fetch cursor document, starting from beginning:', cursorError);
        }
      }

      // Fetch one extra to determine if there are more results
      query = query.limit(pageSize + 1);

      // Execute query
      const snapshot = await query.get();
      const docs = snapshot.docs;

      // Determine pagination state
      const hasMore = docs.length > pageSize;
      const resultDocs = hasMore ? docs.slice(0, pageSize) : docs;

      // Map documents efficiently
      const logs: UserAuditLog[] = resultDocs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as UserAuditLog;
      });

      const lastDocId = resultDocs.length > 0 ? resultDocs[resultDocs.length - 1].id : null;
      const queryTime = Math.round(performance.now() - startTime);

      console.log(`📋 Audit logs query completed in ${queryTime}ms: ${logs.length} logs, hasMore: ${hasMore}`);

      return {
        logs,
        lastDocId,
        hasMore,
        totalFetched: logs.length
      };
    } catch (error) {
      const queryTime = Math.round(performance.now() - startTime);
      console.error(`❌ Audit logs query failed after ${queryTime}ms:`, error?.message || error);

      // Return empty result on error (graceful degradation)
      return {
        logs: [],
        lastDocId: null,
        hasMore: false,
        totalFetched: 0
      };
    }
  }

  /**
   * Get audit log summary
   */
  async getAuditSummary(filter?: AuditLogFilter): Promise<AuditLogSummary> {
    const logs = await this.getAuditLogs(filter).pipe(take(1)).toPromise();

    const summary: AuditLogSummary = {
      total_logs: logs.length,
      security_events: logs.filter(log => log.category === AuditCategory.SECURITY).length,
      failed_attempts: logs.filter(log => log.status === AuditStatus.FAILED).length,
      data_modifications: logs.filter(log => log.category === AuditCategory.DATA_MODIFICATION).length
    };

    // Calculate top actions
    const actionCounts = {};
    logs.forEach(log => {
      actionCounts[log.action_type] = (actionCounts[log.action_type] || 0) + 1;
    });

    summary.top_actions = Object.entries(actionCounts)
      .map(([action, count]) => ({ action: action as AuditActionType, count: count as number }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Calculate top users
    const userCounts = {};
    logs.forEach(log => {
      const key = `${log.user_email}|${log.user_name}`;
      userCounts[key] = (userCounts[key] || 0) + 1;
    });

    summary.top_users = Object.entries(userCounts)
      .map(([userKey, count]) => {
        const [email, name] = userKey.split('|');
        return { user_email: email, user_name: name, action_count: count as number };
      })
      .sort((a, b) => b.action_count - a.action_count)
      .slice(0, 5);

    return summary;
  }

  // Private helper methods

  private async getCurrentUserContext(): Promise<any> {
    // Try to get current user from Firebase Auth first
    const firebaseUser = await this.afAuth.currentUser;

    if (!firebaseUser) {
      return {
        uid: 'anonymous',
        email: 'anonymous@system',
        displayName: 'Anonymous User',
        account_key: this.currentAccount?.account_key
      };
    }

    // Try to get enhanced user data from Firestore
    try {
      const userDoc = await this.afs.collection('users').doc(firebaseUser.uid).get().toPromise();
      const userData = userDoc.exists ? userDoc.data() as User : null;

      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email || userData?.email,
        displayName: firebaseUser.displayName || userData?.displayName || userData?.fullName,
        fullName: userData?.fullName,
        account_key: userData?.account_key || this.currentAccount?.account_key,
        roles: userData?.roles
      };
    } catch (error) {
      // Fallback to Firebase user data only
      console.warn('Could not fetch user data from Firestore, using Firebase Auth data:', error);
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        account_key: this.currentAccount?.account_key
      };
    }
  }

  private getUserRoleString(roles: any): string {
    if (!roles) return 'subscriber';

    if (roles.superAdmin) return 'superAdmin';
    if (roles.admin) return 'admin';
    if (roles.editor) return 'editor';
    if (roles.subscriber) return 'subscriber';
    if (roles.standard) return 'standard';

    return 'unknown';
  }

  private async getClientIP(): Promise<string> {
    try {
      console.log('🌐 Attempting to detect client IP address...');

      // Method 1: Try WebRTC approach (most reliable for local IP)
      console.log('🔍 Trying WebRTC IP detection...');
      const rtcIP = await this.getIPViaWebRTC();
      if (rtcIP && rtcIP !== 'unknown') {
        console.log('✅ WebRTC IP detected:', rtcIP);
        return rtcIP;
      }

      // Method 2: Try external IP service
      console.log('🔍 Trying external IP service...');
      const externalIP = await this.getExternalIP();
      if (externalIP && externalIP !== 'unknown') {
        console.log('✅ External IP detected:', externalIP);
        return externalIP;
      }

      // Method 3: Try to get IP from request headers (if available via proxy)
      console.log('🔍 Checking request headers...');
      const headerIP = this.getIPFromHeaders();
      if (headerIP && headerIP !== 'unknown') {
        console.log('✅ Header IP detected:', headerIP);
        return headerIP;
      }

      console.log('⚠️ Could not detect IP address, using fallback');
      return 'unavailable';
    } catch (error) {
      console.warn('❌ Could not retrieve client IP:', error);
      return 'unknown';
    }
  }

  private async getIPViaWebRTC(): Promise<string> {
    return new Promise((resolve) => {
      try {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });

        pc.createDataChannel('');
        pc.createOffer().then(offer => pc.setLocalDescription(offer));

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            const candidate = event.candidate.candidate;
            const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3})/);
            if (ipMatch) {
              pc.close();
              resolve(ipMatch[1]);
              return;
            }
          }
        };

        // Timeout after 3 seconds
        setTimeout(() => {
          pc.close();
          resolve('unknown');
        }, 3000);

      } catch (error) {
        resolve('unknown');
      }
    });
  }

  private async getExternalIP(): Promise<string> {
    try {
      // Use a free IP detection service
      const response = await fetch('https://api.ipify.org?format=json', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        return data.ip || 'unknown';
      }

      return 'unknown';
    } catch (error) {
      // If external service fails, try backup service
      try {
        const backupResponse = await fetch('https://httpbin.org/ip');
        if (backupResponse.ok) {
          const backupData = await backupResponse.json();
          return backupData.origin?.split(',')[0]?.trim() || 'unknown';
        }
      } catch (backupError) {
        console.warn('External IP services failed:', error, backupError);
      }
      return 'unknown';
    }
  }

  private getIPFromHeaders(): string {
    try {
      // In browser environment, we can't access request headers directly
      // These headers are typically available if behind a proxy/load balancer
      // but would need server-side implementation:
      // - X-Forwarded-For, X-Real-IP, CF-Connecting-IP, X-Client-IP, etc.

      return 'header-unavailable';
    } catch (error) {
      return 'unknown';
    }
  }

  private getSessionId(): string {
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = this.generateSessionId();
      localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  private determineSeverity(actionType: AuditActionType): AuditSeverity {
    switch (actionType) {
      case AuditActionType.DELETE:
      case AuditActionType.BULK_DELETE:
      case AuditActionType.PERMISSION_CHANGE:
      case AuditActionType.ACCOUNT_DELETE:
      case AuditActionType.USER_DELETE:
        return AuditSeverity.HIGH;

      case AuditActionType.LOGIN_FAILED:
      case AuditActionType.UPDATE:
      case AuditActionType.BULK_UPDATE:
      case AuditActionType.SETTINGS_CHANGE:
      case AuditActionType.EXPORT:
      case AuditActionType.ROLE_ASSIGNMENT:
        return AuditSeverity.MEDIUM;

      case AuditActionType.LOGIN:
      case AuditActionType.LOGOUT:
      case AuditActionType.PAGE_VIEW:
      case AuditActionType.READ:
        return AuditSeverity.LOW;

      default:
        return AuditSeverity.MEDIUM;
    }
  }

  private determineCategory(actionType: AuditActionType): AuditCategory {
    switch (actionType) {
      case AuditActionType.LOGIN:
      case AuditActionType.LOGOUT:
      case AuditActionType.LOGIN_FAILED:
      case AuditActionType.PASSWORD_CHANGE:
        return AuditCategory.AUTHENTICATION;

      case AuditActionType.PERMISSION_CHANGE:
      case AuditActionType.ROLE_ASSIGNMENT:
        return AuditCategory.AUTHORIZATION;

      case AuditActionType.CREATE:
      case AuditActionType.UPDATE:
      case AuditActionType.DELETE:
      case AuditActionType.BULK_UPDATE:
      case AuditActionType.BULK_DELETE:
      case AuditActionType.IMPORT:
      case AuditActionType.EXPORT:
        return AuditCategory.DATA_MODIFICATION;

      case AuditActionType.PAGE_VIEW:
      case AuditActionType.NAVIGATION:
        return AuditCategory.SYSTEM_ACCESS;

      case AuditActionType.PAYMENT_PROCESS:
        return AuditCategory.FINANCIAL;

      case AuditActionType.SETTINGS_CHANGE:
      case AuditActionType.ACCOUNT_CREATE:
      case AuditActionType.ACCOUNT_UPDATE:
      case AuditActionType.ACCOUNT_DELETE:
        return AuditCategory.ADMINISTRATION;

      default:
        return AuditCategory.OPERATIONS;
    }
  }

  private getActionDescription(action: AuditActionType): string {
    const descriptions = {
      [AuditActionType.LOGIN]: 'User logged in',
      [AuditActionType.LOGOUT]: 'User logged out',
      [AuditActionType.LOGIN_FAILED]: 'Failed login attempt',
      [AuditActionType.PASSWORD_CHANGE]: 'Password changed',
      [AuditActionType.CREATE]: 'Record created',
      [AuditActionType.READ]: 'Record accessed',
      [AuditActionType.UPDATE]: 'Record updated',
      [AuditActionType.DELETE]: 'Record deleted',
      [AuditActionType.EXPORT]: 'Data exported',
      [AuditActionType.IMPORT]: 'Data imported',
      [AuditActionType.PAGE_VIEW]: 'Page accessed',
      [AuditActionType.SETTINGS_CHANGE]: 'Settings modified'
    };

    return descriptions[action] || 'Action performed';
  }

  private generateChangesSummary(oldValues: any, newValues: any): string {
    if (!oldValues || !newValues) return '';

    const changes = [];
    const keys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);

    keys.forEach(key => {
      if (oldValues[key] !== newValues[key]) {
        changes.push(`${key}: "${oldValues[key]}" → "${newValues[key]}"`);
      }
    });

    return changes.join(', ');
  }
}